import qs from 'qs';
import type { TripsMonitoredUsageData } from '../../types/tripsMonitoredUsage';
import type { TripStatusApiUsageData } from '../../types/tripStatusApiUsage';
import {
  transformTripsAlertsResponse,
  transformTripStatusResponse,
  type TripsAlertsUsageResponse,
  type TripStatusUsageResponse,
  type ApiGranularity,
} from './utils/transformApiResponse';

// Trip Center BFF routes. The Node server injects accountId + user from the
// authenticated session and proxies to the upstream service, so the browser
// never sends them directly.
const BASE = '/api/usage-reporting';

// Map the frontend granularity (lowercase) to the upstream API value (uppercase).
const API_GRANULARITY: Record<string, ApiGranularity> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  quarterly: 'QUARTERLY',
  entireRange: 'MONTHLY',
};

const toApiGranularity = (granularity: string): ApiGranularity =>
  API_GRANULARITY[granularity] || 'MONTHLY';

/**
 * Optimized fetch that uses response.json() for native streaming JSON parsing
 * instead of response.text() + JSON.parse() which double-buffers large payloads.
 */
async function fetchJson<T>(url: string, params: Record<string, string>): Promise<T> {
  const queryString = qs.stringify(params);
  const response = await fetch(`${url}?${queryString}`, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`${response.status}: ${errorBody || 'Request Failed'}`);
  }
  return response.json();
}

/**
 * Fetch Trips & Alerts usage dashboard data.
 * Calls BFF: GET /api/usage-reporting/trips-monitored
 */
export const getTripsMonitoredUsage = async (
  dateStart: string,
  dateEnd: string,
  granularity: string
): Promise<TripsMonitoredUsageData> => {
  const params = { dateStart, dateEnd, granularity: toApiGranularity(granularity) };
  const url = `${BASE}/trips-monitored`;
  try {
    const response = await fetchJson<TripsAlertsUsageResponse>(url, params);
    return transformTripsAlertsResponse(response);
  } catch (error) {
    console.error('[usageReporting] API error GET', url, params, error);
    throw error;
  }
};

/**
 * Fetch Trip Status API usage dashboard data.
 * Calls BFF: GET /api/usage-reporting/trip-status-api
 */
export const getTripStatusApiUsage = async (
  dateStart: string,
  dateEnd: string,
  granularity: string
): Promise<TripStatusApiUsageData> => {
  const params = { dateStart, dateEnd, granularity: toApiGranularity(granularity) };
  const url = `${BASE}/trip-status-api`;
  try {
    const response = await fetchJson<TripStatusUsageResponse>(url, params);
    return transformTripStatusResponse(response);
  } catch (error) {
    console.error('[usageReporting] API error GET', url, params, error);
    throw error;
  }
};
